import { useEffect, useRef, useState } from "react";
import type { ModeConfig } from "../state/modeConfig";
import type { usePlayerState } from "../hooks/usePlayerState";
import { useGameSession } from "../session/useGameSession";
import { TopBar } from "../components/TopBar";
import { Instructions } from "../screens/Instructions";
import { Results } from "../screens/Results";
import { useTranslation } from "../i18n/useTranslation";

type SubPhase = "countdown" | "stimulus" | "gap" | "summary";
type Stimulus = "go" | "nogo";
type Trial = "hit" | "miss" | "false_alarm" | "correct_reject";

const SHOW_MS = 800;
const GAP_MS = 400;

interface GoNoGoProps {
  config: ModeConfig;
  player: ReturnType<typeof usePlayerState>;
  onExit: () => void;
}

export function GoNoGo({ config, player, onExit }: GoNoGoProps) {
  const game = useGameSession(config.modeId, player);
  const { session } = game;
  const { t } = useTranslation();

  const [stimuli, setStimuli] = useState<Stimulus[]>([]);
  const [stimIndex, setStimIndex] = useState(0);
  const [subPhase, setSubPhase] = useState<SubPhase>("countdown");
  const [countdown, setCountdown] = useState(3);
  const [trials, setTrials] = useState<Trial[]>([]);
  const respondedRef = useRef(false);

  const lastRoundRef = useRef(-1);
  useEffect(() => {
    if (session.phase.kind !== "playing") return;
    if (lastRoundRef.current === session.roundNum) return;
    lastRoundRef.current = session.roundNum;
    startRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, session.roundNum]);

  function startRound(): void {
    const nogoRatio = Math.min(0.45, 0.3 + session.difficulty * 0.02);
    const n = 20 + session.difficulty * 2;
    const seq: Stimulus[] = Array.from({ length: n }, () =>
      Math.random() < nogoRatio ? "nogo" : "go",
    );
    setStimuli(seq);
    setStimIndex(0);
    setTrials([]);
    setSubPhase("countdown");
    setCountdown(3);
    respondedRef.current = false;
  }

  useEffect(() => {
    if (subPhase !== "countdown") return;
    if (countdown <= 0) {
      setSubPhase("stimulus");
      respondedRef.current = false;
      return;
    }
    const id = window.setTimeout(() => setCountdown((c) => c - 1), 500);
    return () => window.clearTimeout(id);
  }, [subPhase, countdown]);

  useEffect(() => {
    if (subPhase !== "stimulus") return;
    if (stimIndex >= stimuli.length) return;
    const id = window.setTimeout(() => {
      if (!respondedRef.current) {
        const stim = stimuli[stimIndex];
        setTrials((trs) => [...trs, stim === "go" ? "miss" : "correct_reject"]);
      }
      setSubPhase("gap");
    }, SHOW_MS);
    return () => window.clearTimeout(id);
  }, [subPhase, stimIndex, stimuli]);

  useEffect(() => {
    if (subPhase !== "gap") return;
    const id = window.setTimeout(() => {
      const next = stimIndex + 1;
      if (next >= stimuli.length) {
        setSubPhase("summary");
      } else {
        setStimIndex(next);
        respondedRef.current = false;
        setSubPhase("stimulus");
      }
    }, GAP_MS);
    return () => window.clearTimeout(id);
  }, [subPhase, stimIndex, stimuli]);

  function handleTap(): void {
    if (subPhase !== "stimulus" || respondedRef.current) return;
    if (stimIndex >= stimuli.length) return;
    respondedRef.current = true;
    const stim = stimuli[stimIndex];
    setTrials((trs) => [...trs, stim === "go" ? "hit" : "false_alarm"]);
  }

  function continueFromSummary(): void {
    const hits = trials.filter((tr) => tr === "hit").length;
    const cr = trials.filter((tr) => tr === "correct_reject").length;
    const correct = hits + cr;
    const total = trials.length || 1;
    const acc = correct / total;
    game.submitAnswer(acc >= 0.7);
  }

  if (session.phase.kind === "instructions") {
    return (
      <Instructions
        config={config}
        difficulty={session.difficulty}
        highScore={session.playerState.modeHighScores[config.modeId] ?? 0}
        instructions={[
          t("goNoGo.instr.go"),
          t("goNoGo.instr.noGo"),
          t("goNoGo.instr.run"),
          t("goNoGo.instr.pace"),
        ]}
        difficultyDescription={t("goNoGo.diffDesc", {
          count: 20 + session.difficulty * 2,
          pct: Math.round(Math.min(0.45, 0.3 + session.difficulty * 0.02) * 100),
        })}
        onStart={game.startPlaying}
        onBack={onExit}
        onDifficultyChange={game.setDifficulty}
      />
    );
  }
  if (session.phase.kind === "results") {
    return (
      <Results
        modeName={t(`mode.${config.modeId}.name`)}
        result={session.sessionResult}
        level={session.playerState.level}
        xpProgressFrac={session.playerState.xpProgressFrac}
        onContinue={onExit}
      />
    );
  }

  const hits = trials.filter((tr) => tr === "hit").length;
  const misses = trials.filter((tr) => tr === "miss").length;
  const falseAlarms = trials.filter((tr) => tr === "false_alarm").length;
  const progress = stimuli.length > 0 ? stimIndex / stimuli.length : 0;
  const currentStim = subPhase === "stimulus" ? stimuli[stimIndex] : null;

  return (
    <main className="min-h-screen flex flex-col">
      <TopBar
        modeName={t(`mode.${config.modeId}.name`)}
        score={session.score}
        streak={session.streak.current}
        roundNumber={Math.min(session.roundNum + 1, session.maxRounds)}
        totalRounds={session.maxRounds}
      />
      <div className="h-1 bg-bg-3 mx-5 mt-3 rounded-full overflow-hidden">
        <div
          className="h-full bg-cyan transition-[width] duration-100"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <section
        className="flex-1 flex flex-col items-center justify-center px-6"
        onClick={handleTap}
      >
        {subPhase === "countdown" && (
          <div className="flex flex-col items-center gap-3 select-none">
            <span className="text-amber text-xl font-medium">
              {t("common.getReady")}
            </span>
            <span className="text-7xl font-bold text-fg-0">
              {Math.max(1, countdown)}
            </span>
          </div>
        )}
        {subPhase === "stimulus" && currentStim === "go" && (
          <div className="w-40 h-40 rounded-full bg-green shadow-[0_0_60px_var(--color-green)] flex items-center justify-center text-3xl font-bold text-bg-1 select-none">
            {t("goNoGo.go")}
          </div>
        )}
        {subPhase === "stimulus" && currentStim === "nogo" && (
          <div className="w-40 h-40 rounded-2xl bg-red shadow-[0_0_60px_var(--color-red)] flex items-center justify-center text-3xl font-bold text-bg-1 select-none">
            {t("goNoGo.stop")}
          </div>
        )}
        {subPhase === "gap" && (
          <span className="text-5xl text-fg-3 select-none">+</span>
        )}
        {subPhase === "summary" && (
          <SummaryView
            hits={hits}
            misses={misses}
            falseAlarms={falseAlarms}
            totalTrials={trials.length}
            onContinue={continueFromSummary}
          />
        )}
      </section>

      <footer className="px-5 py-4 flex justify-around font-mono text-xs">
        <Stat label={t("goNoGo.hits")} value={hits} color="text-green" />
        <Stat label={t("goNoGo.missesShort")} value={misses} color="text-red" />
        <Stat label={t("goNoGo.falseAlarms")} value={falseAlarms} color="text-amber" />
      </footer>
      <p className="text-center text-fg-3 text-xs pb-3">
        {t("goNoGo.tapHint")}
      </p>
    </main>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="text-center">
      <div className={`text-lg font-medium ${color}`}>{value}</div>
      <div className="text-fg-3">{label}</div>
    </div>
  );
}

function SummaryView({
  hits,
  misses,
  falseAlarms,
  totalTrials,
  onContinue,
}: {
  hits: number;
  misses: number;
  falseAlarms: number;
  totalTrials: number;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const correct = hits + (totalTrials - hits - misses - falseAlarms);
  const acc = totalTrials > 0 ? Math.round((correct / totalTrials) * 100) : 0;
  const passed = acc >= 70;
  return (
    <div className="flex flex-col items-center gap-5">
      <span className={`text-3xl font-medium ${passed ? "text-green" : "text-red"}`}>
        {passed ? t("goNoGo.sequenceComplete") : t("goNoGo.sequenceFailed")}
      </span>
      <span className="text-fg-1 text-lg">{t("nback.accuracy", { pct: acc })}</span>
      <button
        type="button"
        onClick={onContinue}
        className="px-7 py-2.5 rounded-full bg-cyan text-bg-1 text-sm font-semibold"
      >
        {t("common.continue")}
      </button>
    </div>
  );
}
